import { Controller } from 'stimulus';
import { useEventListener } from '@/js/util';
import Util from '@/js/editor/util';

// Minimum drag distance (px) before the canvas-pan reminder shows, so a bare
// click outside the canvas doesn't trigger it — only an actual drag does.
const PAN_HINT_DRAG_THRESHOLD = 4;

export default class extends Controller {
  connect() {
    this.startCoordinates = { x: 0, y: 0 };
    this.selectionInitiated = false;
    this.dragStartedOutsideCanvas = false;
    this.outlinedItems = new Set();
    this.pendingFrame = null; // required for optimization of rectangle selection

    useEventListener(this);

    this.addEventListener(document, 'visibilitychange', this.toggleGroupSelect);
    this.addEventListener(document, 'keydown', this.toggleGroupSelect);
    this.addEventListener(document, 'keyup', this.toggleGroupSelect);
    this.addEventListener(document, 'mousedown', this.startSelection);
    this.addEventListener(document, 'mouseup', this.endSelection);
    this.addEventListener(document, 'mousemove', this.resizeSelectionBox);
    this.addEventListener(document, 'legacy:stop-selection', this.stopSelection);
  }

  stopSelection = () => {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
    }
    this.pendingFrame = null;
    this.outlinedItems.clear();
    if (this.selectionInitiated) {
      this.dispatch('stop-selection');
    }
    this.endSelection();
  };

  toggleGroupSelect = (e) => {
    const isShiftPressed = e.shiftKey;
    document.querySelector('.item-overlay').dataset.selectionBoxInitiationArea = isShiftPressed;
    const items = document.querySelectorAll('.item:not(.item--locked)');
    items.forEach((item) => {
      item.dataset.selectionBoxInitiationArea = isShiftPressed;
    });
  };

  startSelection = (event) => {
    this.selectionInitiated = event.target.dataset.selectionBoxInitiationArea === 'true';
    if (!this.selectionInitiated) {
      return false;
    }

    document.getElementById('moodboard-right-panel').classList.add('pointer-events-none');
    // an item still loading is hidden, so the box cannot select it
    this.items = Util.boardItems(`${Util.LOADED_ITEMS}:not(.item--locked)`);
    this.itemsCorners = {};
    this.items.forEach((item) => {
      this.itemsCorners[item.e._objProps.boardItemId] =
        window.ed._interaction.getCornersCoordinates(item);
    });

    this.startCoordinates.x = event.clientX;
    this.startCoordinates.y = event.clientY;
    this.element.style.left = `${this.startCoordinates.x}px`;
    this.element.style.top = `${this.startCoordinates.y}px`;

    // Track whether this drag began on the area around the canvas. The pan
    // reminder is dispatched from resizeSelectionBox once the user actually drags,
    // so a bare click doesn't trigger it.
    this.dragStartedOutsideCanvas = event.target.id === 'editor-container';
  };

  endSelection = () => {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
    }
    this.pendingFrame = null;
    if (this.selectionInitiated) {
      window.getSelection().removeAllRanges();
    }
    document.getElementById('moodboard-right-panel').classList.remove('pointer-events-none');
    this.element.classList.add('hidden');
    this.element.style.width = 0;
    this.element.style.height = 0;
    this.selectionInitiated = false;
    this.itemsCorners = {};
    if (this.outlinedItems.size > 0) {
      document.dispatchEvent(new CustomEvent('removeAllOutlines'));

      const items = Array.from(this.outlinedItems);
      this.outlinedItems.clear();

      // Batch-select all items at once to avoid O(N*M) DOM thrashing
      window.ed._shared.selectItems(items);

      // configure text controls if items are present. selectItems skips items while the board is
      // still adding items, so only the ones it actually selected count.
      const textItems = items.filter(
        (item) => item.classList.contains('selected') && item.e._objProps.kind === 'text'
      );
      if (textItems.length > 0) {
        const textProperties = textItems[0].e.textProperties;
        document.dispatchEvent(
          new CustomEvent('legacy:prepare-text-edit-panel', { detail: textProperties })
        );
      }
      window.ed._interaction.placeOverlay();
      window.ed._interaction.updateHandles();
    }
  };

  resizeSelectionBox = (event) => {
    if (!this.selectionInitiated) {
      return;
    }
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame);
    }
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.#updateSelection(event);
    });
  };

  #updateSelection(event) {
    this.element.classList.remove('hidden');
    const deltaX = event.clientX - this.startCoordinates.x;
    const deltaY = event.clientY - this.startCoordinates.y;

    // Remind the user how to pan once the drag actually moves (mirroring the
    // movement spacebar + drag requires), and only for drags that began around
    // the canvas. The key-action toast keeps showing it until the user does
    // space + drag, then suppresses it for the session.
    if (
      this.dragStartedOutsideCanvas &&
      (Math.abs(deltaX) > PAN_HINT_DRAG_THRESHOLD || Math.abs(deltaY) > PAN_HINT_DRAG_THRESHOLD)
    ) {
      this.dragStartedOutsideCanvas = false;
      document.dispatchEvent(
        new CustomEvent('moodboard--toast--key-action:show', { detail: { hint: 'move-canvas' } })
      );
    }

    Object.assign(this.element.style, {
      width: `${Math.abs(deltaX)}px`,
      height: `${Math.abs(deltaY)}px`,
      scale: `${Math.sign(deltaX) || 1} ${Math.sign(deltaY) || 1}`,
    });
    const groupIds = new Set();
    const selectorRect = this.element.getBoundingClientRect();
    const selectorCorners = [
      [selectorRect.left, selectorRect.top],
      [selectorRect.right, selectorRect.top],
      [selectorRect.right, selectorRect.bottom],
      [selectorRect.left, selectorRect.bottom],
    ];

    for (const item of this.items) {
      const itemCorners = this.itemsCorners[item.e._objProps.boardItemId];
      // an item can lose its corners entry while the box is open (DF-3Y7): skip it
      if (!itemCorners) {
        continue;
      }
      if (
        this.#doLinesIntersect(selectorCorners, itemCorners) || // case: if selection box's edge intersects with item's edge
        this.#isPointInsideRect(itemCorners, selectorRect) // case: if item is completely covered by selection box
      ) {
        this.outlinedItems.add(item);
        // outline item
        document.dispatchEvent(
          new CustomEvent('createOutline', {
            detail: {
              itemId: item.e._objProps.boardItemId,
              itemCorners: window.ed._interaction.getCornersCoordinates(item),
            },
          })
        );

        // if item belongs to group - outline whole group
        if (item.dataset.groupId && !groupIds.has(item.dataset.groupId)) {
          groupIds.add(item.dataset.groupId);
          this.#outlineGroup(item.dataset.groupId);
          document.querySelectorAll(`[data-group-id="${item.dataset.groupId}"]`).forEach((item) => {
            document.dispatchEvent(
              new CustomEvent('createOutline', {
                detail: {
                  itemId: item.e._objProps.boardItemId,
                  itemCorners: window.ed._interaction.getCornersCoordinates(item),
                },
              })
            );
          });
        }
      } else {
        // remove outline if item, or group is not under selection box.
        const groupId = item.dataset.groupId;
        if (!event.shiftKey && (!groupId || !groupIds.has(groupId))) {
          this.outlinedItems.delete(item);
          document.dispatchEvent(
            new CustomEvent('removeOutline', {
              detail: { itemId: item.e._objProps.boardItemId },
            })
          );
          if (groupId) {
            document.dispatchEvent(
              new CustomEvent('removeOutline', {
                detail: { itemId: `group-${groupId}` },
              })
            );
          }
        }
      }
    }
  }

  #outlineGroup(groupId) {
    const groupItems = Util.boardItems(`.item[data-group-id="${groupId}"]`);

    document.dispatchEvent(
      new CustomEvent('createOutline', {
        detail: {
          itemId: `group-${groupId}`,
          itemCorners: this.#calculateGroupOutlineCoordinates(groupItems),
          classes: 'group',
        },
      })
    );
  }

  #calculateGroupOutlineCoordinates(group) {
    // find most left/right/top/bottom corners of items in the group
    const extremas = group.reduce(
      (acc, item) => {
        return window.ed._interaction
          .getCornersCoordinates(item)
          .reduce(
            ([minX, minY, maxX, maxY], [x, y]) => [
              Math.min(minX ?? x, x),
              Math.min(minY ?? y, y),
              Math.max(maxX ?? x, x),
              Math.max(maxY ?? y, y),
            ],
            acc
          );
      },
      [null, null, null, null]
    );
    const [minX, minY, maxX, maxY] = extremas;
    const coordinates = [
      [minX, maxY],
      [maxX, maxY],
      [maxX, minY],
      [minX, minY],
    ];
    return coordinates;
  }

  #doLinesIntersect(selectorCorners, itemCorners) {
    for (const [s, selectorCorner] of selectorCorners.entries()) {
      const nextSelectorCorner = selectorCorners[(s + 1) % selectorCorners.length];
      // currnet and next selector's corner points to form a line
      const [sx1, sy1] = selectorCorner;
      const [sx2, sy2] = nextSelectorCorner;

      for (const [i, itemCorner] of itemCorners.entries()) {
        const nextItemCorner = itemCorners[(i + 1) % itemCorners.length];
        // currnet and next item's corner points to form a line
        const [ix1, iy1] = itemCorner;
        const [ix2, iy2] = nextItemCorner;

        // find intersection points of the lines
        const denominator = (sx1 - sx2) * (iy1 - iy2) - (sy1 - sy2) * (ix1 - ix2);
        const t = ((sx1 - ix1) * (iy1 - iy2) - (sy1 - iy1) * (ix1 - ix2)) / denominator;
        const u = -((sx1 - sx2) * (sy1 - iy1) - (sy1 - sy2) * (sx1 - ix1)) / denominator;

        // check if intersection happens on segment where item's and selector's deges are
        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
          return true;
        }
      }
    }
    return false;
  }

  #isPointInsideRect(coordinates, rect) {
    for (const [x, y] of coordinates) {
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return true;
      }
    }
    return false;
  }
}
